/**
 * EVI-1324 helpers for persisting `sourceControlToggleAtom` to the BE.
 *
 * The BE owns the canonical state under
 * `User.evidence_settings.source_control` (see `EvidenceSourceControlSelections`
 * in ml-scribe). The BE schema is snake_case; the FE atom is camelCase. These
 * mappers are the single boundary between the two shapes — keep them in sync
 * with `SourceControlToggleState` (chatAtoms.ts) and the BE Pydantic model.
 *
 * localStorage handling lives in `chatAtoms.ts` via `atomWithStorage`; this
 * file only contains the BE shape + bidirectional mappers used by
 * `useSyncSourceControlSelections`.
 */
import { isEqual } from 'lodash-es';

import {
  SOURCE_CONTROL_DEFAULTS,
  type SourceControlToggleState,
} from '@/atoms/sourceControlAtoms';
import {
  EvidenceSourceControlSelectionsLibrary_mode,
  EvidenceSourceControlSelectionsWeb_collections_mode,
  type components,
} from '@heidi/schema/ml-scribe';
/**
 * Snake-case BE shape, generated from the OpenAPI schema. The FE atom is
 * camelCase; the mappers below are the single boundary between the two
 * shapes. Keep the runtime value space aligned with
 * `SourceControlToggleState` (chatAtoms.ts) — the schema uses string-valued
 * enums (e.g. `EvidenceSourceControlSelectionsLibrary_mode.all === 'all'`)
 * which match the FE atom's string-literal unions at runtime.
 */
export type EvidenceSourceControlSelections =
  components['schemas']['EvidenceSourceControlSelections'];

/**
 * Map the BE snake_case blob into the FE camelCase atom shape.
 * Falls back to `SOURCE_CONTROL_DEFAULTS` when the BE value is `null`/`undefined`
 * (first-time users whose `source_control` has never been written).
 */
export function fromEvidenceSourceControlSelections(
  remote: EvidenceSourceControlSelections | null | undefined
): SourceControlToggleState {
  if (!remote) return SOURCE_CONTROL_DEFAULTS;
  return {
    localGuidelines: remote.local_guidelines ?? SOURCE_CONTROL_DEFAULTS.localGuidelines,
    primaryEvidence: remote.primary_evidence ?? SOURCE_CONTROL_DEFAULTS.primaryEvidence,
    myLibrary: remote.my_library ?? SOURCE_CONTROL_DEFAULTS.myLibrary,
    libraryMode:
      (remote.library_mode as 'all' | 'collections' | undefined) ??
      SOURCE_CONTROL_DEFAULTS.libraryMode,
    selectedCollectionIds:
      remote.selected_collection_ids ?? SOURCE_CONTROL_DEFAULTS.selectedCollectionIds,
    webCollections: remote.web_collections ?? SOURCE_CONTROL_DEFAULTS.webCollections,
    webCollectionsMode:
      (remote.web_collections_mode as 'all' | 'collections' | undefined) ??
      SOURCE_CONTROL_DEFAULTS.webCollectionsMode,
    selectedWebCollectionIds:
      remote.selected_web_collection_ids ??
      SOURCE_CONTROL_DEFAULTS.selectedWebCollectionIds,
    integrationPartners:
      remote.integration_partners ?? SOURCE_CONTROL_DEFAULTS.integrationPartners,
    webSearch: remote.web_search ?? SOURCE_CONTROL_DEFAULTS.webSearch,
    includeUncollected:
      remote.include_uncollected ?? SOURCE_CONTROL_DEFAULTS.includeUncollected,
  };
}

/**
 * Map the FE atom shape into the snake_case BE write payload.
 * Always emits the full blob; the BE shallow-merges `evidence_settings` so
 * partial blobs would silently drop fields.
 */
export function toEvidenceSourceControlSelections(
  local: SourceControlToggleState
): EvidenceSourceControlSelections {
  return {
    local_guidelines: local.localGuidelines,
    primary_evidence: local.primaryEvidence,
    my_library: local.myLibrary,
    library_mode: EvidenceSourceControlSelectionsLibrary_mode[local.libraryMode],
    selected_collection_ids: local.selectedCollectionIds,
    web_collections: local.webCollections,
    web_collections_mode:
      EvidenceSourceControlSelectionsWeb_collections_mode[local.webCollectionsMode],
    selected_web_collection_ids: local.selectedWebCollectionIds,
    integration_partners: local.integrationPartners,
    web_search: local.webSearch,
    include_uncollected: local.includeUncollected,
  };
}

/**
 * Selection for a user who has never saved one (`source_control` is null).
 *
 * `serverDefault` is the backend's flag-gated starting selection
 * (`evidence_settings.default_source_control`, null when the stock default
 * applies). It replaces the cached selection only while that cache is still a
 * default — the stock one, or the server default this browser applied last
 * time — so local-only choices survive, and a default the backend has since
 * changed or withdrawn does not linger. `appliedServerDefault` is what to
 * remember for next time.
 *
 * MOBILE-PARITY-DEBT(EVI-2594, MEVI-192): who gets which default is decided by
 * ml-scribe; applying it while nothing is saved, and following it when it
 * changes, is client-side. iOS/Android mirror: MEVI-192. Until then a mobile
 * user with nothing saved starts from Clinical Web only, and their first edit
 * saves that. Remove when both apps apply `default_source_control` (MEVI-192).
 */
export function resolveUnsavedSourceControl({
  cached,
  serverDefault,
  lastAppliedServerDefault,
}: {
  cached: SourceControlToggleState;
  serverDefault: EvidenceSourceControlSelections | null | undefined;
  lastAppliedServerDefault: SourceControlToggleState | null;
}): {
  next: SourceControlToggleState;
  appliedServerDefault: SourceControlToggleState | null;
} {
  const isCachedDefault =
    isEqual(cached, SOURCE_CONTROL_DEFAULTS) ||
    (lastAppliedServerDefault !== null && isEqual(cached, lastAppliedServerDefault));
  if (!isCachedDefault) {
    return { next: cached, appliedServerDefault: null };
  }
  if (!serverDefault) {
    return { next: SOURCE_CONTROL_DEFAULTS, appliedServerDefault: null };
  }
  const resolved = fromEvidenceSourceControlSelections(serverDefault);
  // Only enabled partners, like the toggle cache, so the two compare equal later.
  const next = {
    ...resolved,
    integrationPartners: Object.fromEntries(
      Object.entries(resolved.integrationPartners).filter(
        ([, enabled]) => enabled === true
      )
    ),
  };
  return { next, appliedServerDefault: next };
}
